import { scenarioRecorder } from './scenarioRecorder';

/**
 * Dedicated diagnostics log for the Test-Records scenario recorder. Separate
 * from general app logging (webLogger / Loki). Emits the frozen single-line
 * format documented in contracts/test-records/README.md:
 *
 *   [recorder] <iso8601-utc> <LEVEL> seq=<n> event=<event> [key=value]...
 *
 * Buffered in memory; the archive-builder card writes the lines to
 * `logs/recorder.log`. A collection warning is surfaced into BOTH the log and
 * scenario.json (via the recorder's warnings) so gaps are explicit.
 */
export type RecorderLogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface RecorderLogEntry {
  seq: number;
  timestamp: string;
  level: RecorderLogLevel;
  event: string;
  fields: Record<string, string | number | boolean>;
}

/** Sink that surfaces a warning into scenario.json (default: the recorder). */
export type WarningSink = (message: string) => void;

function sanitize(value: string | number | boolean): string {
  // No spaces inside a value — the format is space-delimited key=value pairs.
  return String(value).replace(/\s+/g, '_');
}

export class RecorderDiagnosticsLog {
  private seq = 0;
  private readonly entries: RecorderLogEntry[] = [];
  private readonly warningSink: WarningSink;

  constructor(warningSink?: WarningSink) {
    this.warningSink = warningSink ?? ((message) => scenarioRecorder.recordWarning(message));
  }

  // ---- generic ------------------------------------------------------------

  log(level: RecorderLogLevel, event: string, fields: Record<string, string | number | boolean> = {}): RecorderLogEntry {
    const entry: RecorderLogEntry = {
      seq: this.seq,
      timestamp: new Date().toISOString(),
      level,
      event,
      fields,
    };
    this.seq += 1;
    this.entries.push(entry);
    return entry;
  }

  // ---- lifecycle ----------------------------------------------------------

  recordingStarted(mode: string): void {
    this.log('INFO', 'recording.start', { mode });
  }

  recordingStopped(stepCount: number): void {
    this.log('INFO', 'recording.stop', { steps: stepCount });
  }

  stepsCaptured(count: number): void {
    this.log('DEBUG', 'step.captured', { count });
  }

  archiveBuilt(buildMs: number, sizeBytes: number): void {
    this.log('INFO', 'archive.built', { buildMs, sizeBytes });
  }

  // ---- error classes ------------------------------------------------------

  dataCollectionError(detail: string, fields: Record<string, string | number | boolean> = {}): void {
    this.log('ERROR', 'collection.error', { ...fields, detail });
  }

  grpcError(method: string, detail: string): void {
    this.log('ERROR', 'grpc.error', { method, detail });
  }

  revitApiError(detail: string, fields: Record<string, string | number | boolean> = {}): void {
    this.log('ERROR', 'revit.error', { ...fields, detail });
  }

  fileSaveError(path: string, detail: string): void {
    this.log('ERROR', 'file.save.error', { path, detail });
  }

  // ---- partial-failure warning (surfaced to BOTH log + scenario.json) -----

  collectionWarning(message: string, fields: Record<string, string | number | boolean> = {}): void {
    this.log('WARN', 'collection.warning', { ...fields, message });
    this.warningSink(message);
  }

  // ---- output -------------------------------------------------------------

  /** Format one entry as the frozen single line. */
  format(entry: RecorderLogEntry): string {
    const head = `[recorder] ${entry.timestamp} ${entry.level} seq=${entry.seq} event=${entry.event}`;
    const tail = Object.entries(entry.fields)
      .map(([key, value]) => `${key}=${sanitize(value)}`)
      .join(' ');
    return tail.length > 0 ? `${head} ${tail}` : head;
  }

  /** All entries formatted as lines — the `logs/recorder.log` content. */
  lines(): string[] {
    return this.entries.map((entry) => this.format(entry));
  }

  /** Immutable snapshot of the raw entries. */
  getEntries(): RecorderLogEntry[] {
    return this.entries.map((entry) => ({ ...entry, fields: { ...entry.fields } }));
  }

  clear(): void {
    this.seq = 0;
    this.entries.length = 0;
  }
}

/** App-wide diagnostics log; warnings flow into the recorder (→ scenario.json). */
export const recorderDiagnosticsLog = new RecorderDiagnosticsLog();
